import React, {
  createContext,
  useCallback,
  useContext,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import type { UUID } from '@aline/common';
import { routes } from '@aline/routes';
import { matchPath, parsePath } from 'react-router-dom';

import { getIsExternalSession, setExternalSession } from './external-session';
import { useCurrentTabId } from './tabs/tab-active-context';

type ShellMode = 'focused' | 'full';

interface RegistrationCounts {
  focused: number;
  full: number;
}

interface FocusedViewContextValue {
  decisions: ReadonlyMap<UUID, ShellMode>;
  isActiveTabFocused: boolean;
  isExternalSession: boolean;
  markExternalSession: () => void;
  register: (tabId: UUID, mode: ShellMode) => () => void;
}

/**
 * Outside the tabbed app (a guest's task page, isolated component tests) nothing
 * reads the shell, so the default is an inert value rather than a thrown error.
 */
const FocusedViewContext = createContext<FocusedViewContextValue>({
  decisions: new Map(),
  isActiveTabFocused: false,
  isExternalSession: false,
  markExternalSession: () => {},
  register: () => () => {},
});

/**
 * Tracks which tabs show a focused view: the stripped down shell external users
 * get on a task page or a document owned by another org. Tabs stay mounted while
 * hidden, so the decision is kept per tab and the shell reads the active tab's entry.
 *
 * A task or document page decides only once its data loads. Until then the shell
 * keeps the mode it last showed (minimal on a cold start), so the left nav neither
 * flashes in for an external nor out for an internal user.
 *
 * Once the browser tab is an external session, every tab is focused regardless of
 * what its page decides.
 */
export function FocusedViewProvider({
  activeTabId,
  activeTabRoute,
  children,
}: {
  activeTabId: UUID | null;
  activeTabRoute: string | null;
  children: React.ReactNode;
}): JSX.Element {
  const [lastShownMode, setLastShownMode] = useState<ShellMode>('focused');
  const [isExternalSession, setIsExternalSession] = useState(getIsExternalSession);
  const registrationCountsRef = useRef(new Map<UUID, RegistrationCounts>());
  const [decisions, setDecisions] = useState<ReadonlyMap<UUID, ShellMode>>(() => new Map());

  const syncTab = useCallback((tabId: UUID) => {
    const decision = getDecision(registrationCountsRef.current.get(tabId));
    setDecisions(previous => {
      if (previous.get(tabId) === decision) return previous;

      const next = new Map(previous);
      if (decision) next.set(tabId, decision);
      else next.delete(tabId);
      return next;
    });
  }, []);

  const register = useCallback(
    (tabId: UUID, mode: ShellMode) => {
      const allCounts = registrationCountsRef.current;
      const counts = allCounts.get(tabId) ?? { focused: 0, full: 0 };
      allCounts.set(tabId, { ...counts, [mode]: counts[mode] + 1 });
      syncTab(tabId);

      return () => {
        const current = allCounts.get(tabId) ?? { focused: 0, full: 0 };
        const next = { ...current, [mode]: Math.max(current[mode] - 1, 0) };
        if (getDecision(next)) allCounts.set(tabId, next);
        else allCounts.delete(tabId);
        syncTab(tabId);
      };
    },
    [syncTab]
  );

  /**
   * Someone who has already used the full app in this browser tab is an internal user opening
   * another org's task or document, not an external landing from an email link.
   */
  const hasShownFullShellRef = useRef(false);

  const markExternalSession = useCallback(() => {
    if (hasShownFullShellRef.current) return;

    setExternalSession();
    setIsExternalSession(true);
  }, []);

  const activeDecision = activeTabId ? decisions.get(activeTabId) : undefined;
  const isAwaitingDecision = !activeDecision && getIsFocusableRoute(activeTabRoute);
  const decidedMode = activeDecision ?? (isAwaitingDecision ? lastShownMode : 'full');
  const shownMode = isExternalSession ? 'focused' : decidedMode;

  // The render before the first tab exists says nothing about the page, so it is not recorded.
  useLayoutEffect(() => {
    if (!activeTabId || isAwaitingDecision) return;

    setLastShownMode(shownMode);
    if (shownMode === 'full') hasShownFullShellRef.current = true;
  }, [activeTabId, isAwaitingDecision, shownMode]);

  const value = useMemo(
    () => ({
      decisions,
      isActiveTabFocused: shownMode === 'focused',
      isExternalSession,
      markExternalSession,
      register,
    }),
    [decisions, isExternalSession, markExternalSession, register, shownMode]
  );

  return <FocusedViewContext.Provider value={value}>{children}</FocusedViewContext.Provider>;
}

/**
 * Reports whether the tab this component renders in shows a focused view. Pass
 * `null` while the page is still loading the data it decides from.
 *
 * A hidden tab drops its entry and sets it again when shown. A layout effect does
 * that before paint, so the shell never flashes on the way back.
 */
export function useFocusedView(isFocused: boolean | null): void {
  const { register } = useContext(FocusedViewContext);
  const tabId = useCurrentTabId();

  useLayoutEffect(() => {
    if (isFocused === null || !tabId) return;
    return register(tabId, isFocused ? 'focused' : 'full');
  }, [isFocused, register, tabId]);
}

/**
 * Marks the browser tab as an external session once `isExternal` is true, unless the full
 * app has already been shown in it. It stays marked until logout, so a reload or a
 * hand-typed URL keeps the external view.
 */
export function useMarkExternalSession(isExternal: boolean): void {
  const { isExternalSession, markExternalSession } = useContext(FocusedViewContext);

  useLayoutEffect(() => {
    if (isExternal && !isExternalSession) markExternalSession();
  }, [isExternal, isExternalSession, markExternalSession]);
}

export function useIsExternalSession(): boolean {
  return useContext(FocusedViewContext).isExternalSession;
}

/** For shell components above the tabs, such as the left nav and the top bar. */
export function useIsActiveTabFocused(): boolean {
  return useContext(FocusedViewContext).isActiveTabFocused;
}

/** For components rendered inside a tab, such as the editor rail. */
export function useIsFocusedView(): boolean {
  const tabId = useCurrentTabId();
  const { decisions, isExternalSession } = useContext(FocusedViewContext);
  return isExternalSession || Boolean(tabId && decisions.get(tabId) === 'focused');
}

/** Task and document pages, the only routes an external session may open. */
export function getIsFocusableRoute(route: string | null): boolean {
  if (!route) return false;

  const pathname = parsePath(route).pathname ?? '/';
  return Boolean(
    matchPath(routes.TaskLanding, pathname) ||
    matchPath({ end: false, path: routes.DocumentDetail }, pathname)
  );
}

// A focused registration wins over a full one in the same tab.
function getDecision(counts: RegistrationCounts | undefined): ShellMode | null {
  if (!counts) return null;
  if (counts.focused > 0) return 'focused';
  if (counts.full > 0) return 'full';
  return null;
}
